import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { inputCls, labelCls } from "@/components/AuthShell";
import { Megaphone, Clock, X } from "lucide-react";

export function AdminBroadcast() {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: scheduled } = useQuery({ queryKey: ["admin-scheduled"], queryFn: () => apiFetch("/admin/broadcasts/scheduled") });

  const send = async () => {
    if (!title.trim() || !message.trim()) return toast.error("Add a title and message");
    setBusy(true);
    try {
      const body = { title: title.trim(), message: message.trim() };
      if (scheduledAt) body.scheduled_at = new Date(scheduledAt).toISOString();
      const r = await apiFetch("/admin/broadcast", { method: "POST", body: JSON.stringify(body) });
      toast.success(r.scheduled ? "Announcement scheduled" : `Sent to ${r.recipients} users`);
      setTitle(""); setMessage(""); setScheduledAt("");
      qc.invalidateQueries({ queryKey: ["admin-scheduled"] });
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    try { await apiFetch(`/admin/broadcasts/scheduled/${id}/cancel`, { method: "POST" }); qc.invalidateQueries({ queryKey: ["admin-scheduled"] }); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <div className="max-w-lg space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Megaphone className="h-5 w-5" />Send an announcement to all active users.</div>
      <div><label className={labelCls}>Title</label><input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} className={inputCls} data-testid="broadcast-title" /></div>
      <div><label className={labelCls}>Message</label><textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={1000} className={inputCls} data-testid="broadcast-message" /></div>
      <div><label className={labelCls}>Schedule (optional)</label><input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className={inputCls} data-testid="broadcast-schedule" /></div>
      <button onClick={send} disabled={busy} className="rounded-xl bg-price px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="send-broadcast">{busy ? "Sending…" : scheduledAt ? "Schedule" : "Send now"}</button>

      {scheduled?.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 font-semibold">Scheduled</h3>
          <div className="space-y-2">
            {scheduled.map((s) => (
              <div key={s.id} className="flex items-center gap-2 rounded-xl border border-border p-3 text-sm">
                <Clock className="h-4 w-4 text-amber-600" />
                <div className="min-w-0 flex-1"><p className="truncate font-medium">{s.title}</p><p className="text-xs text-muted-foreground">{new Date(s.scheduled_at).toLocaleString()}</p></div>
                <button onClick={() => cancel(s.id)} className="rounded-lg bg-secondary p-1.5" data-testid={`cancel-broadcast-${s.id}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
